/**
 * useSettings -- Platform settings store (Zustand).
 */
import { create } from 'zustand'
import {
  getSettings,
  updateSettings as apiUpdate,
  updateAgentSettings as apiUpdateAgents,
  updatePotRules as apiUpdatePotRules,
  resetSettings as apiReset,
} from '@/lib/api'
import type {
  PlatformSettings,
  AgentSettings,
  TradingSettings,
} from '@/lib/types'

interface SettingsState {
  settings: PlatformSettings | null
  loading: boolean
  saving: boolean
  error: string | null
  dirty: boolean

  fetchSettings: () => Promise<void>
  updateSettings: (body: Partial<PlatformSettings>) => Promise<boolean>
  updateAgentSettings: (body: Partial<AgentSettings>) => Promise<boolean>
  updatePotRules: (body: Partial<TradingSettings>) => Promise<boolean>
  resetToDefaults: () => Promise<boolean>
  setDirty: (v: boolean) => void
}

export const useSettings = create<SettingsState>()((set) => ({
  settings: null,
  loading: false,
  saving: false,
  error: null,
  dirty: false,

  fetchSettings: async () => {
    set({ loading: true, error: null })
    try {
      const settings = await getSettings()
      set({ settings, loading: false })
    } catch (e) {
      set({ error: String(e), loading: false })
    }
  },

  updateSettings: async (body) => {
    set({ saving: true, error: null })
    try {
      const settings = await apiUpdate(body)
      set({ settings, saving: false, dirty: false })
      return true
    } catch (e) {
      set({ error: String(e), saving: false })
      return false
    }
  },

  updateAgentSettings: async (body) => {
    set({ saving: true, error: null })
    try {
      const agents = await apiUpdateAgents(body)
      set(s => ({
        settings: s.settings ? { ...s.settings, agents } : null,
        saving: false,
        dirty: false,
      }))
      return true
    } catch (e) {
      set({ error: String(e), saving: false })
      return false
    }
  },

  updatePotRules: async (body) => {
    set({ saving: true, error: null })
    try {
      const trading = await apiUpdatePotRules(body)
      set(s => ({
        settings: s.settings ? { ...s.settings, trading } : null,
        saving: false,
        dirty: false,
      }))
      return true
    } catch (e) {
      set({ error: String(e), saving: false })
      return false
    }
  },

  resetToDefaults: async () => {
    set({ saving: true, error: null })
    try {
      const result = await apiReset()
      set({ settings: result.settings, saving: false, dirty: false })
      return true
    } catch (e) {
      set({ error: String(e), saving: false })
      return false
    }
  },

  setDirty: (v) => set({ dirty: v }),
}))
